// Universal icon renderer.
//
// Supports three icon systems:
//   type="tabler" (or value starts with "Icon")  → Tabler SVG React component
//   type="fa"    (or fa-/fas/far prefix)         → Font Awesome CSS class
//   default                                       → RemixIcon CSS class (ri-*)
//
// Usage:
//   <Icon value="search-line" />                   → ri-search-line (RemixIcon)
//   <Icon value="fa-heart" />                      → fa-heart (FontAwesome)
//   <Icon type="tabler" value="IconSearch" />      → <IconSearch /> (Tabler SVG)
//   <TablerIcon name="IconSearch" size={20} />     → convenience wrapper

import * as TablerIcons from "@/lib/icons";
import { getRemixIcon } from "@/lib/remixIcon";

const FA_PREFIX_PATTERN = /\bfa-|^(fas|far|fal|fab|fad|fa)\b/;
const TABLER_PREFIX = /^Icon[A-Z]/;

export default function Icon({ value, type, color, size, className, ...rest }) {
  if (!value) return null;

  // --- Tabler SVG icon ---
  const isTabler =
    type === "tabler" || (typeof value === "string" && TABLER_PREFIX.test(value));
  if (isTabler) {
    const Component = TablerIcons[value];
    if (!Component) return null;
    return (
      <Component
        size={size ?? 20}
        color={color ?? "currentColor"}
        className={className}
        {...rest}
      />
    );
  }

  // --- CSS-based icons (FontAwesome / RemixIcon) ---
  const isFontAwesome = type === "fa" || FA_PREFIX_PATTERN.test(value);

  // The RemixIcon stylesheet is no longer loaded, so a bare ri-* class renders
  // nothing. Draw known RemixIcon names with their Tabler equivalent instead.
  const RemixAsTabler = !isFontAwesome && getRemixIcon(value);
  if (RemixAsTabler) {
    return (
      <RemixAsTabler
        size={size ?? 20}
        color={color ?? "currentColor"}
        className={className}
        {...rest}
      />
    );
  }

  const iconClassName = isFontAwesome ? value : `ri-${value}`;

  return (
    <span
      className={[iconClassName, className].filter(Boolean).join(" ")}
      style={{ color: color ?? "inherit", fontSize: size ?? "inherit" }}
      {...rest}
    />
  );
}

export function FAIcon(props) {
  return <Icon {...props} type="fa" />;
}

// Convenience wrapper for Tabler icons — identical to <Icon type="tabler" value="IconName" />
// Usage: <TablerIcon name="IconSearch" size={20} color="#333" />
export function TablerIcon({ name, ...props }) {
  return <Icon type="tabler" value={name} {...props} />;
}
